import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Button, Dropdown, Input, Table } from '../../../../common/ui';
import { setBranchId } from '../../../auth/actions';
import { getSupplierById } from '../../suppliers/actions';
import FilterDropDown from '../../invoices/components/FilterDopdown';
import { createSupplierReturn, loadReturns, loadWeOwe } from '../actions';
import styles from './styles.module.css';
import {
  STATUS_OPTIONS,
  endOfDayMs,
  formatCredit,
  formatMoney,
  formatReturnDate,
  startOfDayMs,
  statusClassName,
} from './format';

const RETURNS_ACTIONS_ENABLED = false;

const FILTER_ITEMS = [
  { name: 'Return No.' },
  { name: 'Date range' },
  { name: 'Status' },
];

const branchOptions = (branchList) =>
  (branchList || []).map((item) => ({ value: item.id, label: item.name }));

const SupplierMerchant = ({
  setBranchId,
  getSupplierById,
  loadWeOwe,
  loadReturns,
  createSupplierReturn,
  auth: { branchList, branchId },
  suppliersState: { searchByIdSupplierData },
  supplierReturnState: { returns, balance, isLoading, loadFailed },
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('owe');
  const [selectedFilterItem, setSelectedFilterItem] = useState(null);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [returnNo, setReturnNo] = useState('');
  const [status, setStatus] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [creating, setCreating] = useState(false);
  const creatingRef = useRef(false);

  const companyName =
    searchByIdSupplierData &&
    !Array.isArray(searchByIdSupplierData) &&
    String(searchByIdSupplierData.id) === String(id)
      ? searchByIdSupplierData.companyname || ''
      : '';

  const filters = useMemo(() => {
    const next = {};
    if (selectedFilterItem === 'Return No.' && returnNo.trim()) {
      next.returnNo = returnNo.trim();
    }
    if (selectedFilterItem === 'Status' && status) {
      next.status = status;
    }
    if (selectedFilterItem === 'Date range') {
      if (fromDate) next.from = startOfDayMs(fromDate);
      if (toDate) next.to = endOfDayMs(toDate);
    }
    return next;
  }, [selectedFilterItem, returnNo, status, fromDate, toDate]);

  useEffect(() => {
    if (id) getSupplierById(id);
  }, [id, getSupplierById]);

  useEffect(() => {
    if (id) loadWeOwe(id);
  }, [id, branchId, loadWeOwe]);

  useEffect(() => {
    if (id) loadReturns(id, filters);
  }, [id, branchId, filters, loadReturns]);

  const columns = [
    { id: 'returnNo', label: 'Return No.', minWidth: 120 },
    { id: 'supplierName', label: 'Supplier', minWidth: 140 },
    {
      id: 'returnDate',
      label: 'Date',
      minWidth: 120,
      format: (value) => formatReturnDate(value),
    },
    {
      id: 'returnValue',
      label: 'Return Value',
      minWidth: 120,
      format: (value) => formatMoney(value),
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 140,
      render: (value) => <span className={statusClassName(styles, value)}>{value}</span>,
    },
    {
      id: 'availableCredit',
      label: 'Available Credit',
      minWidth: 140,
      format: (value) => formatCredit(value),
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.pageTitle}>{companyName || 'Supplier'}</h1>
          <p className={styles.breadcrumb}>
            Inventory / Suppliers / {companyName}
          </p>
        </div>
        {branchList && branchList.length > 0 && branchId && (
          <div className={styles.branchSelectWrap}>
            <Dropdown
              label="Branch"
              options={branchOptions(branchList)}
              value={branchId}
              onChange={setBranchId}
              placeholder="Select branch"
            />
          </div>
        )}
      </div>
      <div className={styles.body}>
        <div className={styles.card}>
          <div className={styles.tabs} role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'owe'}
              className={tab === 'owe' ? `${styles.tab} ${styles.tabActive}` : styles.tab}
              onClick={() => setTab('owe')}
            >
              We owe
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'returns'}
              className={tab === 'returns' ? `${styles.tab} ${styles.tabActive}` : styles.tab}
              onClick={() => setTab('returns')}
            >
              Returns
            </button>
          </div>

          {tab === 'owe' && (
            <div className={styles.owePanel}>
              <p className={styles.oweLabel}>We owe</p>
              <p className={styles.oweValue}>
                {balance === null || balance === undefined ? '' : formatMoney(balance)}
              </p>
            </div>
          )}

          {tab === 'returns' && (
            <div>
              <div className={styles.toolbar}>
                <div className={styles.filterFields}>
                  {selectedFilterItem === 'Return No.' && (
                    <Input
                      label="Return No."
                      value={returnNo}
                      onChange={setReturnNo}
                      placeholder="Return No."
                    />
                  )}
                  {selectedFilterItem === 'Date range' && (
                    <>
                      <Input label="From" type="date" value={fromDate} onChange={setFromDate} />
                      <Input label="To" type="date" value={toDate} onChange={setToDate} />
                    </>
                  )}
                  {selectedFilterItem === 'Status' && (
                    <Dropdown
                      label="Status"
                      options={STATUS_OPTIONS.map((item) => ({ value: item, label: item }))}
                      value={status}
                      onChange={setStatus}
                      placeholder="Status"
                    />
                  )}
                </div>
                <FilterDropDown
                  selectedFilterItem={selectedFilterItem}
                  setSelectedFilterItem={setSelectedFilterItem}
                  showFilterDropdown={showFilterDropdown}
                  setShowFilterDropdown={setShowFilterDropdown}
                  filterDropdownItems={FILTER_ITEMS}
                  disabled={!RETURNS_ACTIONS_ENABLED}
                />
                <Button
                  disabled={!RETURNS_ACTIONS_ENABLED || creating}
                  onClick={async () => {
                    if (creatingRef.current) return;
                    creatingRef.current = true;
                    setCreating(true);
                    try {
                      const created = await createSupplierReturn(id);
                      if (created && created.id) {
                        navigate(`/inventory/supplier-returns/${created.id}`);
                      }
                    } finally {
                      creatingRef.current = false;
                      setCreating(false);
                    }
                  }}
                >
                  Create Supplier Return
                </Button>
              </div>
              <div className={styles.tableSection}>
                <Table
                  columns={columns}
                  rows={returns || []}
                  emptyMessage={
                    isLoading || loadFailed ? '' : 'No supplier returns in this range.'
                  }
                  onRowClick={(row) => navigate(`/inventory/supplier-returns/${row.id}`)}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

SupplierMerchant.propTypes = {
  setBranchId: PropTypes.func.isRequired,
  getSupplierById: PropTypes.func.isRequired,
  loadWeOwe: PropTypes.func.isRequired,
  loadReturns: PropTypes.func.isRequired,
  createSupplierReturn: PropTypes.func.isRequired,
  auth: PropTypes.shape({
    branchList: PropTypes.array,
    branchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  suppliersState: PropTypes.shape({
    searchByIdSupplierData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  }).isRequired,
  supplierReturnState: PropTypes.shape({
    returns: PropTypes.array,
    balance: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
    isLoading: PropTypes.bool,
    loadFailed: PropTypes.bool,
  }).isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.authState,
  suppliersState: state.suppliersState,
  supplierReturnState: state.supplierReturnState,
});

export default connect(mapStateToProps, {
  setBranchId,
  getSupplierById,
  loadWeOwe,
  loadReturns,
  createSupplierReturn,
})(SupplierMerchant);
